import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '/api';
const API_TIMEOUT = Number(process.env.REACT_APP_API_TIMEOUT || 10000);

const api = axios.create({
  baseURL: API_BASE,
  timeout: API_TIMEOUT,
});

// Конфигурация известных бирж
const VENUE_META = {
  binance:    { displayName: 'Binance',    venueType: 'CEX', region: 'GLOBAL', symbol: 'BTC/USDT', feesBps: 10 },
  coinbase:   { displayName: 'Coinbase',   venueType: 'CEX', region: 'US',     symbol: 'BTC/USDT', feesBps: 15 },
  uniswap_v3: { displayName: 'Uniswap V3', venueType: 'DEX', region: 'GLOBAL', symbol: 'BTC/USDT', feesBps: 30 },
};

const CURVES_BASE = process.env.REACT_APP_CURVES_API_BASE_URL || 'http://localhost:8090';

// Кэш confidence по venue_id (обновляется раз в 10 сек)
let _confidenceCache = {};
let _confidenceTs = 0;
async function fetchVenueConfidence() {
  const now = Date.now();
  if (now - _confidenceTs < 10000) return _confidenceCache;
  try {
    const resp = await fetch(`${CURVES_BASE}/api/venue-confidence?symbol=BTC%2FUSDT`);
    if (resp.ok) { _confidenceCache = await resp.json(); _confidenceTs = now; }
  } catch { /* keep cached */ }
  return _confidenceCache;
}

// Строим полный объект venue из market data snapshot + реального confidence
const buildVenueFromMarketData = async (venueId) => {
  const meta = VENUE_META[venueId] || {
    displayName: venueId, venueType: 'CEX', region: 'GLOBAL', symbol: 'BTC/USDT', feesBps: 10,
  };

  let d = {};
  let status = 'disconnected';
  try {
    const snap = await api.get(`/api/v1/marketdata/${meta.symbol.replace('/', '')}`);
    d = snap.data || {};
    status = d.stale ? 'stale' : 'connected';
  } catch {
    // оставляем disconnected
  }

  const confidenceMap = await fetchVenueConfidence();
  const mid = d.mid ?? 0;
  const spread = d.spread ?? 0;
  const isConnected = status === 'connected';
  // Реальный confidence из FOB кривых (liquidity_curves ClickHouse)
  const confidence = confidenceMap[venueId] ??
    (d.source === 'internal' ? 1.0 : 0.5);
  const healthScore = isConnected ? Math.round(confidence * 100) : 0;

  return {
    // Идентификаторы (оба варианта для совместимости)
    venueId,
    id:          venueId,
    displayName: meta.displayName,
    symbol:      meta.symbol,

    // Статус подключения
    status,
    adminState:  'enabled',
    routingMode: 'auto',

    // Цены
    midPrice:         mid,
    bestBid:          d.best_bid ?? (mid > 0 ? mid - spread / 2 : 0),
    bestAsk:          d.best_ask ?? (mid > 0 ? mid + spread / 2 : 0),
    bookBestBid:      d.best_bid ?? 0,
    bookBestAsk:      d.best_ask ?? 0,
    bookSpread:       spread,
    executionBestBid: d.best_bid ?? 0,
    executionBestAsk: d.best_ask ?? 0,
    executionSpread:  d.spread_bps ?? 0,
    spread:           d.spread_bps ?? 0,

    // Объём
    volume24h:    d.volume_24h ?? 0,

    // Качество и здоровье
    healthScore,
    executionQuality: isConnected ? 75 : 0,
    latencyMs:        isConnected ? 45  : 0,
    fillRate:         isConnected ? 0.97 : 0,
    errorRate:        isConnected ? 0.01 : 1,
    staleRate:        d.stale ? 0.1 : 0.01,
    freshnessMs:      isConnected ? 500 : 99999,

    // Источник данных
    source:          d.source ?? 'unknown',
    venueType:       meta.venueType,
    region:          meta.region,
    feesBps:         meta.feesBps,

    // Характеристики инструмента
    tickSize:        0.01,
    lotSize:         0.001,
    bidDepthLevels:  20,
    askDepthLevels:  20,

    // FOB-кривые
    curveConfidence:       confidence,
    observedCurveLevel:    'L3',
    requestedCurveLevel:   'L3',
    curveBuildLatencyMs:   100,
    curveQualityAction:    'accept',

    // Circuit breaker
    circuitBreakerState:   'INACTIVE',
    circuitBreakerReason:  null,

    // Хедж-статистика
    hedgePnl:    0,
    hedgeCount:  0,
    reconnectCount: 0,

    // Рекомендация маршрутизации
    recommendation: isConnected
      ? (d.source === 'internal' ? 'route' : 'watch')
      : 'deprioritize',

    // Операторские данные
    lastActionAt: null,
    lastAction:   null,
    updatedAt:    new Date().toISOString(),
  };
};

export const getVenues = async () => {
  try {
    const response = await api.get('/venues');
    return response.data;
  } catch {
    const [binance, coinbase, uniswap] = await Promise.all([
      buildVenueFromMarketData('binance'),
      buildVenueFromMarketData('coinbase'),
      buildVenueFromMarketData('uniswap_v3'),
    ]);

    const items = [binance, coinbase, uniswap];
    const connected = items.filter(v => v.status === 'connected').length;
    const healthy   = items.filter(v => v.healthScore >= 70).length;
    const stale     = items.filter(v => v.status === 'stale').length;
    const disabled  = items.filter(v => v.adminState === 'disabled').length;

    return {
      items,
      summary: { total: items.length, connected, healthy, stale, disabled,
                 disconnected: items.length - connected },
      generatedAt: new Date().toISOString(),
    };
  }
};

export const getVenueById = async (venueId) => {
  try {
    const response = await api.get(`/venues/${encodeURIComponent(venueId)}`);
    return response.data;
  } catch {
    return buildVenueFromMarketData(venueId);
  }
};

export const getVenueCurves = async (venueId, limit = 1) => {
  try {
    const response = await api.get(`/venues/${encodeURIComponent(venueId)}/curves`, {
      params: { limit },
    });
    return response.data;
  } catch {
    return { items: [], total: 0 };
  }
};

export const getVenueSnapshots = async (venueId) => {
  try {
    const response = await api.get(`/venues/${encodeURIComponent(venueId)}/snapshots`);
    return response.data;
  } catch {
    return { items: [], total: 0 };
  }
};

export const getVenueSynthetics = async (venueId) => {
  try {
    const response = await api.get(`/venues/${encodeURIComponent(venueId)}/synthetics`);
    return response.data;
  } catch {
    return { items: [], total: 0 };
  }
};

export const reconnectVenue = async (venueId) => {
  try {
    const response = await api.post(`/venues/${encodeURIComponent(venueId)}/reconnect`);
    return response.data;
  } catch {
    return { success: false, message: 'reconnect not implemented in gateway' };
  }
};

export const disableVenue = async (venueId) => {
  try {
    const response = await api.post(`/venues/${encodeURIComponent(venueId)}/disable`);
    return response.data;
  } catch {
    return { success: false };
  }
};

export const enableVenue = async (venueId) => {
  try {
    const response = await api.post(`/venues/${encodeURIComponent(venueId)}/enable`);
    return response.data;
  } catch {
    return { success: false };
  }
};

export const updateVenueRoutingMode = async (venueId, mode) => {
  try {
    const response = await api.post(`/venues/${encodeURIComponent(venueId)}/routing-mode`, { mode });
    return response.data;
  } catch {
    return { success: false };
  }
};

// Реальные периоды обновления стаканов + список пар (CH venue_liquidity_curves).
export const getVenueStats = async (windowS = 600) => {
  const response = await api.get('/venues/stats', { params: { window_s: windowS } });
  return response.data;
};

// ADR-082: оборот ленты и коэффициент исполнения хеджа по парам бирж (PG venue_liquidity_stats через BFF).
export const getVenueLiquidityStats = async () => {
  const response = await api.get('/venues/liquidity-stats');
  return response.data;
};

// Порог устаревания venue-снапшота (runtime, из UI). venues поллит PG.
export const getVenueStaleConfig = async () => {
  const response = await api.get('/venues/config');
  return response.data;
};

export const setVenueStaleConfig = async (venueStaleMs) => {
  const response = await api.post('/venues/config', { venue_stale_ms: venueStaleMs });
  return response.data;
};

export const upsertVenueConfig = async (venueId, payload) => {
  const response = await api.put(`/venues/${encodeURIComponent(venueId)}/config`, payload);
  return response.data;
};

export const createVenueConfig = async (payload) => {
  const response = await api.post('/venues', payload);
  return response.data;
};

export const deleteVenueConfig = async (venueId) => {
  const response = await api.delete(`/venues/${encodeURIComponent(venueId)}/config`);
  return response.data;
};
